import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Transaction } from '../../../services/types';
import { getCurrencySymbol } from '../../../services/utils';
import {
  PincoScreen,
  PINCO_RED,
  PINCO_YELLOW,
  PINCO_CARD_BG,
  PINCO_CARD_BG_SOFT,
  PINCO_TEXT_MUTED,
} from '../components/PincoLayout';
import type { DeclineContext } from '../../../services/types';

const secondsLeft = (expireAt?: string): number | null => {
  if (!expireAt) return null;
  const ms = new Date(expireAt).getTime() - Date.now();
  return ms > 0 ? Math.floor(ms / 1000) : 0;
};
const mmss = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const money = (n: number, cur?: string) =>
  `${n.toLocaleString('ru-RU')}${getCurrencySymbol(cur) ?? (cur ? ` ${cur}` : '')}`;

const Title = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 20px;
  font-weight: 500;
  color: ${PINCO_YELLOW};
  margin: 4px 0 8px;
`;

const Hint = styled.div`
  font-family: 'PT Mono', monospace;
  font-size: 13px;
  color: ${PINCO_TEXT_MUTED};
  line-height: 1.55;
  margin-bottom: 18px;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;

const Timer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: #fff;
  background: ${PINCO_CARD_BG};
  border: 1px solid rgba(233, 192, 95, 0.35);
  border-radius: 999px;
  padding: 7px 16px;
  margin-bottom: 20px;
  & b {
    color: ${PINCO_YELLOW};
    animation: ${pulse} 1.4s ease-in-out infinite;
    letter-spacing: 1px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

// Rectangular amount tile — same casino look (gold dashed ring on dark, red when picked)
const Tile = styled.button<{ $selected: boolean }>`
  padding: 18px 8px;
  border-radius: 14px;
  cursor: pointer;
  font-family: 'PT Mono', monospace;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: ${({ $selected }) =>
    $selected
      ? `linear-gradient(160deg, #ff3b52, ${PINCO_RED})`
      : `linear-gradient(160deg, ${PINCO_CARD_BG_SOFT}, ${PINCO_CARD_BG})`};
  border: none;
  outline: 1.5px dashed ${({ $selected }) => ($selected ? 'rgba(255,255,255,0.7)' : 'rgba(233,192,95,0.5)')};
  outline-offset: -6px;
  box-shadow: ${({ $selected }) =>
    $selected ? `0 0 0 1.5px ${PINCO_RED}, 0 0 18px rgba(255,1,35,0.55)` : '0 3px 10px rgba(0,0,0,0.4)'};
  transition: transform 0.12s, box-shadow 0.2s;
  &:hover:not(:disabled) { transform: translateY(-2px); }
  &:active:not(:disabled) { transform: scale(0.96); }
  &:disabled { cursor: default; opacity: 0.6; }
`;

// ─── Footer — reuse the requisite-page pattern (two buttons in one row) ───────
const RefuseBtn = styled.button`
  width: 100%;
  margin-bottom: 6px;
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: #9a9a9a;
  font-family: 'PT Sans', sans-serif;
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
  &:hover { color: #ffffff; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const FooterBtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 18px;
  margin-bottom: 10px;
  @media (max-width: 575px) { flex-direction: column-reverse; }
`;

const PayBtn = styled.button`
  background-color: #ffffff;
  color: #000;
  border: 1px solid transparent;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'PT Sans', sans-serif;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; box-shadow: none; cursor: default; }
`;

interface Props {
  transaction: Transaction;
  uuid: string;
  timeLeft: string;
  /** Cancel as a refusal of the offered amounts. */
  onDeclineOffer: (context: DeclineContext) => Promise<void> | void;
  /** Cancel survey items, in order; empty — no survey. */
  onSelectVariant?: (variantId: number) => void;
  variantSubmitting?: boolean;
}

const PincoVariantPage = ({ transaction, uuid, timeLeft, onDeclineOffer, onSelectVariant, variantSubmitting }: Props) => {
  const { t } = useTranslation();
  const tx = transaction as any;
  const variants: { id: number; amount: number }[] = tx.variants ?? [];
  const currency = tx.currency ?? tx.requisite?.currency ?? 'RUB';

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [left, setLeft] = useState<number | null>(secondsLeft(tx.variantsExpireAt));
  const [declining, setDeclining] = useState(false);

  // The only way out of this screen besides picking an amount: the ordinary cancel, recorded as a
  // refusal of the offered amounts.
  const declineVariants = async () => {
    if (declining) return;
    setDeclining(true);
    try {
      await onDeclineOffer('variants');
    } finally {
      setDeclining(false);
    }
  };

  useEffect(() => {
    if (!tx.variantsExpireAt) return;
    const id = setInterval(() => setLeft(secondsLeft(tx.variantsExpireAt)), 1000);
    return () => clearInterval(id);
  }, [tx.variantsExpireAt]);

  return (
    <PincoScreen
      step={1}
      withReceipt={transaction.simpleConfirmation === false}
      uuid={uuid}
      timeLeft={timeLeft}
      footerSlot={
        <>
          <FooterBtnRow>
            <PayBtn
              disabled={selectedId === null || variantSubmitting}
              onClick={() => selectedId !== null && onSelectVariant?.(selectedId)}
            >
              {t('variant.pay', 'Пополнить')}
            </PayBtn>
          </FooterBtnRow>
          <RefuseBtn
            type='button'
            data-sentry-element='variant-refuse'
            disabled={declining || variantSubmitting}
            onClick={declineVariants}
          >
            {t('variant.refuse')}
          </RefuseBtn>
        </>
      }
    >
      <Title>{t('variant.selectAmount', 'Выберите сумму пополнения')}</Title>
      <Hint>
        {t('variant.intro', {
          amount: money(tx.amount, currency),
          defaultValue: 'Пока не удалось подобрать реквизит под сумму {{amount}}. Ниже есть реквизиты под другие суммы — выберите подходящую, и мы зачислим именно её.',
        })}
      </Hint>

      {left !== null && (
        <Timer>
          {t('variant.offerLeft', 'Осталось выбрать')}: <b>{mmss(left)}</b>
        </Timer>
      )}

      <Grid>
        {variants.map((v) => (
          <Tile
            key={v.id}
            type='button'
            $selected={selectedId === v.id}
            disabled={variantSubmitting}
            onClick={() => setSelectedId(v.id)}
          >
            {money(v.amount, currency)}
          </Tile>
        ))}
      </Grid>
    </PincoScreen>
  );
};

export default PincoVariantPage;
